// Why a piece came back, the state it came back in and how the money goes
// back. The reason is the store's own return reason (tillData.returnReasons,
// cs_returnreason) and travels as cart.returnReasonId, which the order
// processor saves on the bill (s_order.cs_returnreason), exactly as the
// SuperMarket reason drawer does. Condition and refund-to have no column of
// their own, so they ride in returnSummary.returnRemarks, the free-text note
// the same drawer writes.
import useCartStore from "../../stores/useCartStore";

export const RETURN_CONDITIONS = [
  { value: "UNWORN", label: "Unworn, tags on" },
  { value: "TRIED", label: "Tried on" },
  { value: "WORN", label: "Worn" },
  { value: "DAMAGED", label: "Damaged" },
];

const TENDER_FALLBACK = ["Cash", "Card", "UPI"];

// Reasons the till synced, in the order the store set them up.
export function returnReasonsOf(tillData) {
  return (tillData?.returnReasons || []).filter((r) => r && r.returnReasonId && r.name);
}

// How the bill was paid, by tender name: "UPI", "Cash + Card". Falls back to
// the usual tenders when the bill doesn't say.
export function refundTenders(order) {
  const paid = [...(order?.paymentData || []), ...(order?.payments || [])]
    .map((p) => (typeof p === "string" ? p : p?.name || p?.paymentMethod || p?.paymentmethod))
    .filter(Boolean)
    .map((n) => String(n).trim());
  const unique = [...new Set(paid)];
  if (unique.length === 0) return { paidBy: "", paid: [], options: TENDER_FALLBACK };
  return { paidBy: unique.join(" + "), paid: paidAmounts(order), options: [...new Set([...unique, "Cash"])] };
}

// [{ name, amount }] per tender on the bill (amounts summed by name); empty
// when the bill doesn't carry its payment amounts.
function paidAmounts(order) {
  const sums = new Map();
  for (const p of [...(order?.paymentData || []), ...(order?.payments || [])]) {
    if (!p || typeof p !== "object") continue;
    const name = String(p.name || p.paymentMethod || p.paymentmethod || "").trim();
    const amount = Number(p.amount);
    if (!name || !Number.isFinite(amount) || amount <= 0) continue;
    sums.set(name, (sums.get(name) || 0) + amount);
  }
  return [...sums].map(([name, amount]) => ({ name, amount }));
}

// "Condition: Unworn, tags on · Refund to: UPI · Note text"
export function returnRemarks({ condition, refundTo, note } = {}) {
  const cond = RETURN_CONDITIONS.find((c) => c.value === condition)?.label;
  return [cond && `Condition: ${cond}`, refundTo && `Refund to: ${refundTo}`, String(note || "").trim()].filter(Boolean).join(" · ");
}

// What gets written on the cart.
export function returnNoteFields({ reasonId, condition, refundTo, note } = {}) {
  const remarks = returnRemarks({ condition, refundTo, note });
  const out = {};
  if (reasonId) out.returnReasonId = reasonId;
  if (remarks) out.returnSummary = { returnRemarks: remarks };
  return out;
}

const isReturnOf = (cart, billIds) =>
  Boolean(cart && (cart.isReturn === true || cart.isReturn === "Y") && (cart.items || []).some((i) => i?.sOrderReturnId && billIds.has(i.sOrderReturnId)));

const stampCart = (cart, fields) => ({
  ...cart,
  ...fields,
  returnSummary: fields.returnSummary ? { ...(cart.returnSummary || {}), ...fields.returnSummary } : cart.returnSummary,
});

let pending = null;

// The return lines reach the cart later (straight away for a plain refund,
// after Core's options modal otherwise), so the note waits for the first
// return cart from this bill and is written onto it once. Another bill or a
// cancel drops it.
export function stampReturnNote(fields, billIds, { timeoutMs = 10 * 60 * 1000 } = {}) {
  clearReturnNote();
  const ids = new Set((billIds || []).filter(Boolean));
  if (ids.size === 0 || Object.keys(fields || {}).length === 0) return false;
  const tryStamp = () => {
    const st = useCartStore.getState();
    let done = false;
    if (isReturnOf(st.cart, ids)) {
      st.setCart((c) => stampCart(c, fields));
      done = true;
    }
    if (isReturnOf(st.exchangeCart, ids)) {
      st.setExchangeCart((c) => stampCart(c, fields));
      done = true;
    }
    return done;
  };
  if (tryStamp()) return true;
  const unsubscribe = useCartStore.subscribe((state, prev) => {
    if (state.cart === prev?.cart && state.exchangeCart === prev?.exchangeCart) return;
    if (pending && (isReturnOf(state.cart, ids) || isReturnOf(state.exchangeCart, ids))) {
      clearReturnNote();
      tryStamp();
    }
  });
  const timer = setTimeout(clearReturnNote, timeoutMs);
  pending = { unsubscribe, timer };
  return true;
}

export function clearReturnNote() {
  if (!pending) return;
  pending.unsubscribe();
  clearTimeout(pending.timer);
  pending = null;
}
